interface LinkDocumentToEventParams {
  eventId: string;
  documentId: string;
  /**
   * The document id of the canvas/diagram related to this document (resolved
   * via the document's recording, role = "canvas"), or null when there is no
   * related canvas. When present it is linked to the same event as a
   * best-effort extra — a failure here must not fail the primary doc link.
   */
  canvasDocumentId: string | null;
  /**
   * The surface this link is scoped to, or null for a document-level link.
   * Applies to the primary document link only — a canvas has no surfaces of
   * its own, so its link always stays document-level.
   */
  surfaceId: string | null;
  /** The calendar store's linkDocument(eventId, documentId, surfaceId) action. */
  linkDocument: (eventId: string, documentId: string, surfaceId: string | null) => Promise<void>;
}

/**
 * Links a document to a calendar event, and — if the document has a related
 * canvas/diagram — links that canvas to the same event too.
 *
 * The primary document link is awaited and propagates errors to the caller.
 * The canvas link is best-effort: if it fails we swallow the error so the
 * document still ends up linked (the canvas is an optional convenience).
 */
export async function linkDocumentToEvent({
  eventId,
  documentId,
  canvasDocumentId,
  surfaceId,
  linkDocument,
}: LinkDocumentToEventParams): Promise<void> {
  await linkDocument(eventId, documentId, surfaceId);

  if (canvasDocumentId) {
    try {
      await linkDocument(eventId, canvasDocumentId, null);
    } catch {
      // Best-effort — the document is already linked; a failed canvas link
      // should not surface as a failure of the whole action.
    }
  }
}
